import React, { useState, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import get from 'lodash/get';

import CategoryHeader from './CategoryHeader';

import colors from '../../../../styling/colors';
import typography from '../../../../styling/mixins/typography';
import withTrackingPropsProvider from '../../../../hocs/withTrackingPropsProvider';

const FullWidthWrapper = styled.nav`
  ${typography.BasisFont};
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  height: fit-content;

  & .nav-drawer {
    min-height: 0;
    max-height: 0;
    opacity: 0;
    transition: max-height 0.5s ease-in-out, opacity 0.5s ease-in-out;
  }

  &.nav-open .nav-drawer {
    max-height: 1000px;
    opacity: 1;
  }
`;

const CategoryList = styled.ul`
  height: 48px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 0;
  padding: 0 2rem;
  list-style: none;
  border-bottom: 1px solid ${colors('gallery')};
`;

const TrackedFullWidthWrapper = withTrackingPropsProvider(FullWidthWrapper);

const TRACKING_PROPS = {
  block_name: 'category_nav',
  block_row: 'category_nav',
};

const CategoryNav = memo(
  ({ openNav, closeNav, nodes, onNodeClick: parentClickHandler }) => {
    const [activeCategory, setActiveCategory] = useState(undefined);
    const [isOpen, setIsOpen] = useState(false);

    let categoryFocused = category => {
      const hasChildren = !!category.childNodes.length;
      const willOpen = !isOpen && hasChildren;

      setActiveCategory(category);
      setIsOpen(hasChildren);

      if (willOpen && openNav) {
        openNav();
      }
    };

    let menuBlurred = () => {
      let currentlyOpen = isOpen;

      setIsOpen(false);

      if (currentlyOpen && closeNav) {
        closeNav();
      }
    };

    let onNodeClick = props => {
      menuBlurred();
      parentClickHandler(props);
    };

    let activeCategoryId = get(activeCategory, 'id', false);

    let categoriesList = nodes.map((node, index) => {
      const isFocused = node && activeCategoryId === node.id;

      return (
        <CategoryHeader
          node={node}
          isFocused={isFocused}
          onFocused={categoryFocused}
          onNodeClick={onNodeClick}
        />
      );
    });

    return (
      <TrackedFullWidthWrapper
        className={isOpen ? 'nav-open' : ''}
        tracking={TRACKING_PROPS}
        onMouseLeave={menuBlurred}
      >
        <CategoryList>{categoriesList}</CategoryList>
      </TrackedFullWidthWrapper>
    );
  },
);

CategoryNav.propTypes = {
  closeNav: PropTypes.func,
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      childNodes: PropTypes.array,
    }),
  ).isRequired,
  onNodeClick: PropTypes.func.isRequired,
  openNav: PropTypes.func,
};

CategoryNav.defaultProps = {
  nodes: [],
};

export default CategoryNav;
